'use client';
import React from 'react';
import Link from 'next/link';
import Header from '@/components/Header';
import Footer from '@/components/Footer';

const sections = [
  {
    title: 'Information We Collect',
    content: [
      {
        subtitle: 'Information You Provide Directly',
        text: 'When you book a service, join our waitlist, or contact us, we collect information you voluntarily provide — including your name, email address, phone number, vehicle details (make, model, year), and service location (address or general area in Mesquite, NV). If you submit a photo quote request, we also receive any photos you upload.',
      },
      {
        subtitle: 'Information Collected Automatically',
        text: 'When you visit our website, we may automatically collect certain technical information such as your IP address, browser type, device type, operating system, referring URLs, and pages visited. This information helps us understand how visitors use our site and improve your experience.',
      },
      {
        subtitle: 'Communications',
        text: 'If you contact us via email, text, or Instagram, we retain records of those communications to provide support and follow up on your service requests.',
      },
    ],
  },
  {
    title: 'How We Use Your Information',
    content: [
      {
        subtitle: 'Service Delivery',
        text: 'We use your contact and vehicle information to schedule and perform mobile detailing services at your location, send booking confirmations, and communicate about your appointment.',
      },
      {
        subtitle: 'Waitlist & Booking Management',
        text: 'Your email and contact details are used to manage our waitlist, notify you when booking slots open, and send service reminders or follow-up messages.',
      },
      {
        subtitle: 'Quote Requests',
        text: 'Photos and vehicle details submitted through our quote form are used solely to prepare an accurate service estimate. We do not share these with third parties.',
      },
      {
        subtitle: 'Business Operations',
        text: 'We may use aggregated, anonymized data to understand service demand, improve our offerings, and make operational decisions. This data cannot be used to identify you personally.',
      },
    ],
  },
  {
    title: 'How We Share Your Information',
    content: [
      {
        subtitle: 'We Do Not Sell Your Data',
        text: 'Vertex Premium Mobile Detailing does not sell, rent, or trade your personal information to third parties for marketing purposes — ever.',
      },
      {
        subtitle: 'Service Providers',
        text: 'We work with a small number of trusted service providers who help us operate our business — including email delivery services (Resend) and our database infrastructure (Supabase). These providers are contractually obligated to protect your data and may only use it to perform services on our behalf.',
      },
      {
        subtitle: 'Legal Requirements',
        text: 'We may disclose your information if required by law, court order, or governmental authority, or if we believe disclosure is necessary to protect the rights, property, or safety of Vertex Premium Mobile Detailing, our customers, or others.',
      },
    ],
  },
  {
    title: 'Data Retention',
    content: [
      {
        subtitle: 'How Long We Keep Your Data',
        text: 'We retain your personal information for as long as necessary to provide our services and fulfill the purposes described in this policy. Booking records and contact information are generally kept for up to 2 years to support repeat service requests and warranty-related follow-ups. You may request deletion of your data at any time by contacting us directly.',
      },
    ],
  },
  {
    title: 'Your Rights & Choices',
    content: [
      {
        subtitle: 'Access & Correction',
        text: 'You have the right to request access to the personal information we hold about you and to request corrections if any information is inaccurate or incomplete.',
      },
      {
        subtitle: 'Deletion',
        text: 'You may request that we delete your personal information from our records. We will honor such requests unless we are required to retain the data for legal or legitimate business purposes.',
      },
      {
        subtitle: 'Opt-Out of Communications',
        text: 'You may opt out of non-essential communications (such as promotional messages) at any time by replying STOP to any text message or contacting us directly. Note that transactional messages related to your active bookings cannot be opted out of while a service is pending.',
      },
      {
        subtitle: 'California Residents (CCPA)',
        text: 'If you are a California resident, you have additional rights under the California Consumer Privacy Act, including the right to know what personal information is collected, the right to delete personal information, and the right to opt out of the sale of personal information. We do not sell personal information. To exercise your rights, contact us at the information below.',
      },
    ],
  },
  {
    title: 'Cookies & Tracking',
    content: [
      {
        subtitle: 'Website Cookies',
        text: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience, remember your preferences, and analyze site traffic. You can control cookie settings through your browser preferences. Disabling cookies may affect certain features of the site.',
      },
      {
        subtitle: 'Analytics',
        text: 'We may use analytics tools to understand how visitors interact with our website. Any data collected through analytics is aggregated and does not personally identify you.',
      },
    ],
  },
  {
    title: 'Security',
    content: [
      {
        subtitle: 'How We Protect Your Data',
        text: 'We implement reasonable technical and organizational measures to protect your personal information against unauthorized access, alteration, disclosure, or destruction. Our data infrastructure uses industry-standard encryption and access controls. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.',
      },
    ],
  },
  {
    title: 'Third-Party Links',
    content: [
      {
        subtitle: 'External Sites',
        text: 'Our website may contain links to third-party websites such as Instagram. We are not responsible for the privacy practices of those sites. We encourage you to review the privacy policies of any third-party sites you visit.',
      },
    ],
  },
  {
    title: 'Changes to This Policy',
    content: [
      {
        subtitle: 'Policy Updates',
        text: 'We may update this Privacy Policy from time to time to reflect changes in our practices or applicable law. When we make material changes, we will update the "Last Updated" date at the top of this page. Your continued use of our services after any changes constitutes your acceptance of the updated policy.',
      },
    ],
  },
  {
    title: 'Contact Us',
    content: [
      {
        subtitle: 'Questions or Requests',
        text: 'If you have any questions about this Privacy Policy, want to exercise your data rights, or need to reach us for any privacy-related matter, please contact us:',
      },
    ],
    contact: true,
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: '#0a0a0f' }}>
      <Header />
      <main className="flex-1 relative">
        {/* Ambient background */}
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] pointer-events-none"
          style={{ background: 'radial-gradient(ellipse, rgba(45,95,154,0.08) 0%, transparent 70%)', filter: 'blur(60px)' }}
        />

        <div className="relative z-10 max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-20">
          {/* Back link */}
          <Link
            href="/home"
            className="inline-flex items-center gap-2 mono text-[10px] tracking-widest uppercase mb-10 transition-colors duration-200"
            style={{ color: 'rgba(168,169,173,0.5)' }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#4A7EC0')}
            onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(168,169,173,0.5)')}
          >
            <span>←</span>
            <span>Back to Home</span>
          </Link>

          {/* Header */}
          <div className="mb-12 md:mb-16">
            <div className="inline-flex items-center gap-2 mb-4">
              <span className="mono text-[9px] tracking-widest uppercase px-2.5 py-1 rounded-full" style={{ background: 'rgba(45,95,154,0.12)', border: '1px solid rgba(74,126,192,0.2)', color: '#4A7EC0' }}>
                Legal
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-3" style={{ color: '#F5F5F7' }}>
              Privacy Polic<span style={{ color: '#4A7EC0' }}>y</span>
            </h1>
            <p className="mono text-[10px] tracking-widest uppercase" style={{ color: 'rgba(168,169,173,0.4)' }}>
              Last Updated: August 2026
            </p>
            <p className="mt-5 text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.7)' }}>
              Vertex Premium Mobile Detailing ("we," "us," or "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, share, and protect your personal information when you use our website or services. By using our services, you agree to the practices described in this policy.
            </p>
          </div>

          {/* Divider */}
          <div className="w-full h-px mb-12" style={{ background: 'linear-gradient(90deg, transparent, rgba(74,126,192,0.3), transparent)' }} />

          {/* Sections */}
          <div className="space-y-10 md:space-y-14">
            {sections?.map((section, i) => (
              <div key={i}>
                <div className="flex items-center gap-3 mb-5">
                  <span
                    className="mono text-[9px] font-bold tracking-widest w-6 h-6 rounded flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(45,95,154,0.15)', border: '1px solid rgba(74,126,192,0.2)', color: '#4A7EC0' }}
                  >
                    {i + 1}
                  </span>
                  <h2 className="text-base md:text-lg font-semibold tracking-tight" style={{ color: '#F5F5F7' }}>
                    {section?.title}
                  </h2>
                </div>

                <div className="pl-9 space-y-5">
                  {section?.content?.map((item, j) => (
                    <div key={j}>
                      <h3 className="mono text-[10px] font-semibold tracking-widest uppercase mb-2" style={{ color: '#4A7EC0' }}>
                        {item?.subtitle}
                      </h3>
                      <p className="text-sm leading-relaxed" style={{ color: 'rgba(168,169,173,0.75)' }}>
                        {item?.text}
                      </p>
                    </div>
                  ))}

                  {section?.contact && (
                    <div
                      className="mt-4 p-4 rounded-xl space-y-2"
                      style={{ background: 'rgba(45,95,154,0.07)', border: '1px solid rgba(74,126,192,0.15)' }}
                    >
                      <p className="mono text-[10px] font-semibold tracking-widest uppercase mb-3" style={{ color: 'rgba(168,169,173,0.5)' }}>
                        Vertex Premium Mobile Detailing
                      </p>
                      <div className="space-y-2">
                        <a
                          href="tel:+17252711448"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">☎</span>
                          725-271-1448
                        </a>
                        <a
                          href="mailto:envizionary.studios@gmail.com"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">✉</span>
                          envizionary.studios@gmail.com
                        </a>
                        <a
                          href="https://www.instagram.com/vertexpremiummobiledetailing"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-2 text-sm transition-colors duration-200"
                          style={{ color: '#4A7EC0' }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#F5F5F7')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                        >
                          <span className="mono text-[10px]">◈</span>
                          @vertexpremiummobiledetailing
                        </a>
                        <p className="text-xs" style={{ color: 'rgba(168,169,173,0.5)' }}>
                          Mesquite, Nevada
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {i < sections?.length - 1 && (
                  <div className="mt-10 md:mt-14 w-full h-px" style={{ background: 'rgba(168,169,173,0.07)' }} />
                )}
              </div>
            ))}
          </div>

          {/* Footer note */}
          <div className="mt-16 pt-8" style={{ borderTop: '1px solid rgba(168,169,173,0.07)' }}>
            <p className="mono text-[10px] tracking-wide text-center" style={{ color: 'rgba(168,169,173,0.3)' }}>
              This policy applies to Vertex Premium Mobile Detailing services in Mesquite, Nevada and surrounding areas.
            </p>
            <div className="flex items-center justify-center gap-4 mt-4">
              <Link
                href="/terms"
                className="mono text-[10px] tracking-widest uppercase transition-colors duration-200"
                style={{ color: 'rgba(74,126,192,0.6)' }}
                onMouseEnter={(e) => (e.currentTarget.style.color = '#4A7EC0')}
                onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(74,126,192,0.6)')}
              >
                Terms & Conditions →
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
